import { css } from 'aphrodite';
import moment from 'moment';
import { parseAsIsoDate, parseAsString, parseAsStringEnum, useQueryState, useQueryStates } from 'nuqs';
import * as React from 'react';
import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import { DealStageOptions, DemoStatusFilter } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../models/NameUtils';
import { useLeadDemosQuery } from '../../../queries/Lead/useLeadDemos';
import { useLeadDemosToConfirmQuery } from '../../../queries/Lead/useLeadDemosToConfirmQuery';
import { Button } from '../../../web/components-v2/Button';
import { Dialog } from '../../../web/components-v2/Dialog';
import { Databoard, DataboardBody } from '../../../web/components/Databoard';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../web/components/DataGrid';
import { ErrorMessageModal } from '../../../web/components/errorMessages/ErrorMessageModal';
import { ExportConfirmationModalV2 } from '../../../web/components/ExportConfirmation';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { bs } from '../../../web/styles/styles';
import { BulkTranscriptDownloadBanner } from '../../components/BulkTranscriptDownloadBanner';
import { DemoRecordingsModal, IDemoRecording } from '../../components/DemoRecordingsModal';
import { useSingleDemoAnalysis } from '../../components/DemoRecordingsModal/useSingleDemoAnalysis';
import { OutcomeInfo } from '../../components/leadView/components/OutcomeInfo';
import { RecordingIndicator } from '../../components/leadView/components/RecordingIndicator';
import { TextColumn } from '../../components/leadView/components/TextColumn';
import { DemoDateWithRecording } from '../../components/leadView/LeadViewRowElement/components/DemoDateWithRecording';
import { styleSheet } from '../../components/leadView/LeadViewRowElement/styles';
import { useExportTranscriptsMutation } from '../../queries/useExportTranscripts';
import { destructive, gold, green } from '../../styles/colors';
import { FieldKey } from '../../viewModels/form';
import { ILeadSearchView, IPhoneCallLogView } from '../../viewModels/leads/interfaces';
import { LeadServedSource } from '../../viewModels/queue';
import { DemoAnalysisModal } from './Analysis/components/DemoAnalysisModal';
import { useBulkTranscriptAnalysis } from './Analysis/hooks/useBulkTranscriptAnalysis';
import { Filters } from './Filters';

// Get attempts display text
const getAttemptsText = (searchView: ILeadSearchView) => {
	const confirmationAttempts = searchView.nextMeeting?.confirmationAttempts || 0;
	if (confirmationAttempts === 0) return 'No Attempts';
	return `${confirmationAttempts} Attempt${confirmationAttempts > 1 ? 's' : ''}`;
};

// Get attempts color based on confirmation status and attempt count
const getAttemptsColor = (searchView: ILeadSearchView) => {
	// Determine if demo is confirmed - check if the outcome name is "Demo Confirmed"
	const isDemoConfirmed = searchView.lastInteraction?.outcome?.name === 'Demo Confirmed';
	if (isDemoConfirmed) {
		return green; // Green for confirmed demos
	}
	const confirmationAttempts = searchView.nextMeeting?.confirmationAttempts || 0;
	// For unconfirmed demos, use traffic light system
	if (confirmationAttempts <= 1) {
		return destructive; // Red for 0-1 attempts
	} else if (confirmationAttempts === 2) {
		return gold; // Yellow for 2 attempts
	} else {
		return green; // Green for 3+ attempts
	}
};

export const DemosContainer: React.FC = () => {
	const id = React.useId();
	const [userId] = useQueryState('userId', parseAsString);
	const [dateRange] = useQueryStates({
		from: parseAsIsoDate,
		to: parseAsIsoDate,
	});
	const [demoStatus] = useQueryState(
		'demoStatus',
		parseAsStringEnum<DemoStatusFilter>(Object.values(DemoStatusFilter)).withDefault(DemoStatusFilter.DemosToConfirm)
	);
	const [outcomeName] = useQueryState('outcomeName', parseAsStringEnum<FieldKey>(Object.values(FieldKey)));
	const [managerUserId] = useQueryState('managerUserId', parseAsString);
	const [demoPerformerUserId] = useQueryState('demoPerformerUserId', parseAsString);
	const [companyId] = useQueryState('companyId', parseAsString);
	let dealStage: DealStageOptions[] = [];
	switch (demoStatus) {
		case DemoStatusFilter.DemosToReschedule:
			dealStage = [DealStageOptions.AppointmentNoShow];
			break;
		case DemoStatusFilter.UpcomingDemos:
			dealStage = [DealStageOptions.AppointmentScheduled, DealStageOptions.AppointmentRescheduled];
			break;
		default:
			break;
	}
	const dataSource =
		dealStage.length === 0 && demoStatus !== DemoStatusFilter.PastDemos && !companyId ? 'demosToConfirm' : 'demos';
	const leadDemosToConfirmQuery = useLeadDemosToConfirmQuery({
		enabled: dataSource === 'demosToConfirm',
		userId,
		managerUserId,
		companyId,
		outcomeName: outcomeName !== FieldKey.Unknown ? outcomeName : undefined,
		demoPerformerUserId,
		start: dateRange.from,
		end: dateRange.to,
		meta: {
			toastErrorMessage: 'An unexpected error occurred while loading demos.',
			errorLogging: {
				category: 'DemosContainer',
				action: 'useLeadDemosToConfirmQuery',
			},
		},
	});
	const leadDemosQuery = useLeadDemosQuery({
		enabled: dataSource === 'demos',
		userId,
		managerUserId,
		companyId,
		outcomeName: outcomeName !== FieldKey.Unknown ? outcomeName : undefined,
		demoPerformerUserId,
		start: dateRange.from,
		end: dateRange.to,
		dealStage,
		meta: {
			toastErrorMessage: 'An unexpected error occurred while loading demos.',
			errorLogging: {
				category: 'DemosContainer',
				action: 'useLeadDemosQuery',
			},
		},
	});
	const activeQuery = dataSource === 'demosToConfirm' ? leadDemosToConfirmQuery : leadDemosQuery;
	const isLoading = activeQuery.isLoading;
	const isSuccess = activeQuery.isSuccess;
	const isEmpty = activeQuery.isSuccess && activeQuery.data.pages[0].values.length === 0;

	const [recordingsModal, setRecordingsModal] = useState<{
		demoId?: string;
		demoDate?: string;
	}>({});

	// Single demo analysis hook for analyzing individual recordings from the modal
	const {
		isAnalysisModalOpen: isSingleAnalysisModalOpen,
		startAnalysis: startSingleAnalysis,
		closeAnalysis: closeSingleAnalysis,
		isLoading: isSingleAnalysisLoading,
		isCreatingConversation: isSingleAnalysisCreatingConversation,
		sendFollowUp: sendSingleAnalysisFollowUp,
		conversationId: singleAnalysisConversationId,
		aiPromptMutation: singleAnalysisAiPromptMutation,
	} = useSingleDemoAnalysis();

	const [analyzingRecordingId, setAnalyzingRecordingId] = useState<string | null>(null);

	const handleRecordingClick = (demoId: string, demoDate: string, _closerOwnerId: string) => {
		setRecordingsModal({
			demoId,
			demoDate,
		});
	};

	const handleCloseModal = () => {
		setRecordingsModal({ demoId: undefined, demoDate: undefined });
	};

	const handleStartSingleAnalysis = (recording: IDemoRecording) => {
		setAnalyzingRecordingId(recording.uuid);
		startSingleAnalysis(recording);
		// Close the recordings modal when starting analysis
		handleCloseModal();
	};

	const handleCloseSingleAnalysis = () => {
		setAnalyzingRecordingId(null);
		closeSingleAnalysis();
	};

	if (isLoading) {
		return (
			<DemosLayout>
				<LoadingSpinner type='large' />
			</DemosLayout>
		);
	}
	if (isEmpty) {
		return (
			<DemosLayout>
				<div className={css(bs.textCenter, bs.p2)}>No data to show</div>
			</DemosLayout>
		);
	}

	if (isSuccess) {
		const data = activeQuery.data.pages.flatMap(page => page.values);

		return (
			<DemosLayout>
				<Databoard styles={[bs.overflowHidden]}>
					<DataboardBody>
						<DataGrid>
							<DataGridColumns>
								<DataGridColumn>Demo perform date</DataGridColumn>
								<DataGridColumn>Company info</DataGridColumn>
								<DataGridColumn>SDR</DataGridColumn>
								<DataGridColumn>Last call</DataGridColumn>
								<DataGridColumn>Recording</DataGridColumn>
							</DataGridColumns>
							<DataGridRows>
								{data.map((item, index) => {
									return (
										<DataGridRow key={`${id}-lead-search-row-${index}`}>
											<DataGridRowItem>
												<DemoDateWithRecording
													dateValue={item.nextMeeting?.startDate ?? item.lastDeal?.demoPerformDate}
													demoId={item.company?.id}
													closerOwnerId={item.lastDeal?.creator?.id}
													dealStage={item.lastDeal?.stage}
													onRecordingClick={handleRecordingClick}
												/>
											</DataGridRowItem>
											<DataGridRowItem>
												{item.company?.id ? (
													<Button asChild variant='link' styles={[styleSheet.companyLink]}>
														<NavLink
															to={`/queue?from=${LeadServedSource.AppointmentConfirmation}&companyId=${item.company?.id}`}
														>
															<TextColumn
																text={item.company?.companyName || 'Unknown'}
																subtext={item.company?.phoneNumbers[0]?.metadata?.standard || 'Unknown'}
															/>
														</NavLink>
													</Button>
												) : (
													<TextColumn
														text={item.company?.companyName || 'Unknown'}
														subtext={item.company?.phoneNumbers[0]?.metadata?.standard || 'Unknown'}
													/>
												)}
											</DataGridRowItem>
											<DataGridRowItem>
												<TextColumn
													text={item.lastDeal?.creator ? getDisplayName(item.lastDeal.creator) : 'Unknown'}
													isSmall={true}
												/>
											</DataGridRowItem>
											<DataGridRowItem>
												<div>
													<OutcomeInfo
														name={item.lastInteraction?.outcome?.name}
														color={item.lastInteraction?.outcome?.hexColor}
														date={item.lastInteraction?.date}
														meetingParticipants={item.nextMeeting?.participants}
													/>
													<div className={css(styleSheet.lastInteraction)}>
														<div className={css(styleSheet.dot)} style={{ background: getAttemptsColor(item) }} />
														<span>{getAttemptsText(item)}</span>
													</div>
												</div>
											</DataGridRowItem>
											<DataGridRowItem>
												<RecordingIndicator
													creationDate={(item as IPhoneCallLogView).phoneCall?.creationDate}
													creatorName={getDisplayName((item as IPhoneCallLogView).phoneCall?.creator)}
													phoneNumber={(item as IPhoneCallLogView).phoneCall?.phoneNumber?.standard}
													id={(item as IPhoneCallLogView).phoneCall?.id}
													publicUrl={(item as IPhoneCallLogView).phoneCall?.recording?.publicUrl}
												/>
											</DataGridRowItem>
										</DataGridRow>
									);
								})}
								{activeQuery.hasNextPage && !activeQuery.isFetchingNextPage ? (
									<Waypoint key='load-more-trigger' bottomOffset='-200px' onEnter={() => activeQuery.fetchNextPage()} />
								) : null}
							</DataGridRows>
						</DataGrid>
						{activeQuery.isFetchingNextPage ? (
							<div className={css(bs.flex, bs.justifyCenter, bs.p2)}>
								<LoadingSpinner type='small' />
							</div>
						) : null}
					</DataboardBody>
					<Dialog
						open={Boolean(recordingsModal.demoId)}
						onOpenChange={open => {
							if (!open) {
								handleCloseModal();
							}
						}}
					>
						<DemoRecordingsModal
							demoId={recordingsModal.demoId}
							demoDate={recordingsModal.demoDate}
							onStartAnalysis={handleStartSingleAnalysis}
							analyzingRecordingId={analyzingRecordingId}
							isAnalyzeLoading={isSingleAnalysisLoading}
						/>
					</Dialog>
					{isSingleAnalysisModalOpen && singleAnalysisConversationId && (
						<DemoAnalysisModal
							isOpen={isSingleAnalysisModalOpen}
							onClose={handleCloseSingleAnalysis}
							isLoading={isSingleAnalysisLoading}
							isCreatingConversation={isSingleAnalysisCreatingConversation}
							onSendFollowUp={sendSingleAnalysisFollowUp}
							conversationId={singleAnalysisConversationId}
							aiPromptMutation={singleAnalysisAiPromptMutation}
						/>
					)}
				</Databoard>
			</DemosLayout>
		);
	}
};

function DemosLayout({ children }: { children: React.ReactNode }) {
	const userSession = useUserSession();
	const exportTranscriptsMutation = useExportTranscriptsMutation();
	const [demoStatus] = useQueryState(
		'demoStatus',
		parseAsStringEnum<DemoStatusFilter>(Object.values(DemoStatusFilter)).withDefault(DemoStatusFilter.DemosToConfirm)
	);
	const [demoPerformerUserId] = useQueryState('demoPerformerUserId', parseAsString);
	const [dateRange] = useQueryStates({
		from: parseAsIsoDate,
		to: parseAsIsoDate,
	});

	const isPastDemosSelected = demoStatus === DemoStatusFilter.PastDemos;
	const hasCloserOwner = Boolean(demoPerformerUserId);
	const hasDateRange = Boolean(dateRange.from && dateRange.to);
	const isStartDateTodayOrPast = dateRange.from ? moment(dateRange.from).isSameOrBefore(moment(), 'day') : false;
	const isCoffeeAccount = userSession?.account?.isLevitateSalesCoffeeAccount;

	const isEligible = isPastDemosSelected && hasCloserOwner && hasDateRange && isStartDateTodayOrPast && isCoffeeAccount;

	// Bulk transcript analysis
	const startDate = dateRange.from ? moment(dateRange.from).startOf('day').toISOString() : '';
	const endDate = dateRange.to ? moment(dateRange.to).endOf('day').toISOString() : '';

	const {
		isAnalysisModalOpen,
		startBulkAnalysis,
		closeAnalysis,
		isLoading: isLoadingAnalysis,
		isLoadingTranscripts,
		isCreatingConversation,
		sendFollowUp,
		conversationId,
		aiPromptMutation,
	} = useBulkTranscriptAnalysis({
		userId: demoPerformerUserId || '',
		startDate,
		endDate,
		source: 'demos_page_bulk',
	});

	const handleExportClick = () => {
		if (!dateRange.from || !dateRange.to || !demoPerformerUserId) {
			return;
		}
		exportTranscriptsMutation.mutate({
			userId: demoPerformerUserId,
			startDate,
			endDate,
		});
	};

	const handleAnalyzeClick = () => {
		startBulkAnalysis();
	};

	const handleExportModalClose = () => {
		exportTranscriptsMutation.reset();
	};

	return (
		<>
			<div className={css(styleSheet.root)}>
				<div className={css(styleSheet.container)}>
					{isEligible && demoPerformerUserId && (
						<BulkTranscriptDownloadBanner
							onExportClick={handleExportClick}
							onAnalyzeClick={handleAnalyzeClick}
							isAnalyzeLoading={isLoadingTranscripts}
						/>
					)}
					<div className={css(styleSheet.containerInner)}>
						<div className={css(styleSheet.content)}>
							<div className={css(styleSheet.header)}>
								<h2 className={css(styleSheet.title)}>Demos</h2>
							</div>
							<Filters />
							{children}
						</div>
					</div>
				</div>
				{/* <SideDrawer>
					<CoffeeDrawerToolbar>
						<div />
					</CoffeeDrawerToolbar>
				</SideDrawer> */}
			</div>
			{exportTranscriptsMutation.error ? (
				<ErrorMessageModal
					modalProps={{
						isOpen: !exportTranscriptsMutation.isIdle || exportTranscriptsMutation.error !== null,
						onRequestClose: handleExportModalClose,
					}}
					title='Export Failed'
				>
					<div>
						{exportTranscriptsMutation.error?.message || 'An unexpected error occurred while exporting transcripts.'}
					</div>
				</ErrorMessageModal>
			) : (
				<ExportConfirmationModalV2
					mutationResult={exportTranscriptsMutation}
					modalProps={{
						isOpen: !exportTranscriptsMutation.isIdle,
						onRequestClose: handleExportModalClose,
					}}
					title='Export Meeting Transcripts'
					documentKind='transcript archive'
					subTitle={
						dateRange.from && dateRange.to ? (
							<p>
								Exporting transcripts from {moment(dateRange.from).format('MM/DD/YYYY')} to{' '}
								{moment(dateRange.to).format('MM/DD/YYYY')}
							</p>
						) : null
					}
				/>
			)}

			{/* Demo Analysis Modal */}
			<DemoAnalysisModal
				isOpen={isAnalysisModalOpen}
				onClose={closeAnalysis}
				isLoading={isLoadingAnalysis}
				isCreatingConversation={isCreatingConversation}
				onSendFollowUp={sendFollowUp}
				conversationId={conversationId}
				aiPromptMutation={aiPromptMutation}
			/>
		</>
	);
}
